import {
  ApartmentCategoryEnum,
  HouseCategoryEnum,
  LandCategoryEnum,
  ListingTypeEnum,
  OfficeCategoryEnum,
  PurposeEnum,
  TypeEnum,
} from "@/types/types";
import { z } from "zod";

const ZodPurposeEnum = z.enum([PurposeEnum.FORSALE, PurposeEnum.FORRENT]);
const ZodTypeEnum = z.enum([
  TypeEnum.APARTMENT,
  TypeEnum.HOUSE,
  TypeEnum.LAND,
  TypeEnum.OFFICE,
]);

const ZodApartmentCategoryEnum = z.enum(
  [
    ApartmentCategoryEnum.GARDEN,
    ApartmentCategoryEnum.LOFT,
    ApartmentCategoryEnum.PENTHOUSE,
    ApartmentCategoryEnum.STANDARD,
    ApartmentCategoryEnum.STUDIO,
  ],
  { errorMap: () => ({ message: "apartment_category_error" }) }
);

const ZodHouseCategoryEnum = z.enum(
  [
    HouseCategoryEnum.BUNGALOW,
    HouseCategoryEnum.DETACHED,
    HouseCategoryEnum.DUPLEX,
    HouseCategoryEnum.MANSION,
    HouseCategoryEnum.SEMI_DETACHED,
    HouseCategoryEnum.TOWNHOUSE,
    HouseCategoryEnum.VILLA,
  ],
  { errorMap: () => ({ message: "house_category_error" }) }
);

const ZodLandCategoryEnum = z.enum(
  [
    LandCategoryEnum.AGRICULTURAL,
    LandCategoryEnum.COMERCIAL,
    LandCategoryEnum.INDUSTRIAL,
    LandCategoryEnum.MIX_USE,
    LandCategoryEnum.RESIDENTIAL,
  ],
  { errorMap: () => ({ message: "land_category_error" }) }
);

const ZodOfficeCategoryEnum = z.enum(
  [
    OfficeCategoryEnum.COMERCIAL,
    OfficeCategoryEnum.INDUSTRIAL,
    OfficeCategoryEnum.OFFICE,
  ],
  { errorMap: () => ({ message: "office_category_error" }) }
);

// const interiorDetailsSchema = z.object({
//   rooms: z.string().optional(),
//   appliances: z.string().optional(),
//   more: z.string().optional(),
// });

// const exteriorDetailsSchema = z.object({
//   parking: z.string().optional(),
//   outdoors: z.string().optional(),
//   more: z.string().optional(),
// });

// const otherDetailsSchema = z.object({
//   community_hoa: z.string().optional(),
//   financial: z.string().optional(),
//   more: z.string().optional(),
// });

const baseSchema = z.object({
  listing_id: z.string().min(10),
  purpose: ZodPurposeEnum,
  type: ZodTypeEnum,
});

export const getSchema = (
  type: TypeEnum,
  purpose: PurposeEnum,
  listing_type: ListingTypeEnum
) => {
  let schema = baseSchema;
  if (type === TypeEnum.APARTMENT) {
    schema = schema.extend({
      category: ZodApartmentCategoryEnum,
    });
  }
  if (type === TypeEnum.HOUSE) {
    schema = schema.extend({
      category: ZodHouseCategoryEnum,
    });
  }
  if (type === TypeEnum.LAND) {
    schema = schema.extend({
      category: ZodLandCategoryEnum,
    });
  }
  if (type === TypeEnum.OFFICE) {
    schema = schema.extend({
      category: ZodOfficeCategoryEnum,
    });
  }
  schema = schema.extend({
    price: z.number().min(1, "priceTooLow"),
    title: z.string().min(20, "titleTooShort").max(100, "titleTooLong"),
    web_tour_url: z.string().url("url-invalid").optional().or(z.literal("")),
    presentation_video_url: z
      .string()
      .url("url-invalid")
      .optional()
      .or(z.literal("")),
  });
  if (listing_type === ListingTypeEnum.ESSENTIAL) {
    schema = schema.extend({
      photos: z
        .array(z.string().url())
        .min(3, "notEnoughPhotos")
        .max(20, "tooManyPhotos"),
    });
  }
  if (listing_type === ListingTypeEnum.PLUS) {
    schema = schema.extend({
      photos: z
        .array(z.string().url())
        .min(3, "notEnoughPhotos")
        .max(40, "tooManyPhotos"),
    });
  }
  if (listing_type === ListingTypeEnum.PRIME) {
    schema = schema.extend({
      photos: z
        .array(z.string().url())
        .min(3, "notEnoughPhotos")
        .max(60, "tooManyPhotos"),
    });
  }
  if (listing_type === ListingTypeEnum.SPOTLIGHT) {
    schema = schema.extend({
      photos: z
        .array(z.string().url())
        .min(3, "notEnoughPhotos")
        .max(80, "tooManyPhotos"),
    });
  }
  if (type === TypeEnum.LAND && purpose === PurposeEnum.FORSALE) {
    schema = schema.extend({
      lot_size: z.number().min(1, "lotSizeTooLow"),
      buyers_fee: z
        .number()
        .min(0, "buyersFeeInvalid")
        .max(30, "buyersFeeInvalid"),
    });
  }
  if (type === TypeEnum.LAND && purpose === PurposeEnum.FORRENT) {
    schema = schema.extend({
      lot_size: z.number().min(1, "lotSizeTooLow"),
    });
  }
  if (
    (type === TypeEnum.APARTMENT || type === TypeEnum.HOUSE) &&
    purpose === PurposeEnum.FORSALE
  ) {
    schema = schema.extend({
      number_of_rooms: z.number().min(1, "roomsTooLow"),
      number_of_full_bathrooms: z.number().min(0),
      number_of_bathrooms: z.number().min(1, "bathroomsTooLow"),
      number_of_bedrooms: z.number().min(1, "bedroomsTooLow"),
      interior_space: z.number().min(1, "interiorTooLow"),
      levels: z.string().min(1, "levelsTooLow"),
      total_structure_area: z.number().min(1, "totalStructureTooLow"),
      lot_size:
        type === TypeEnum.APARTMENT
          ? z.number().optional()
          : z.number().min(1, "lotSizeTooLow"),
      buyers_fee: z
        .number()
        .min(0, "buyersFeeInvalid")
        .max(30, "buyersFeeInvalid"),
      year_built: z
        .number()
        .min(1900, "yearBuiltInvalid")
        .max(new Date().getFullYear() + 6, "yearBuiltInvalid"),
      construction_materials: z.string().optional(),
    });
  }
  if (
    (type === TypeEnum.APARTMENT || type === TypeEnum.HOUSE) &&
    purpose === PurposeEnum.FORRENT
  ) {
    schema = schema.extend({
      number_of_rooms: z.number().min(1, "roomsTooLow"),
      number_of_full_bathrooms: z.number().min(0),
      number_of_bathrooms: z.number().min(1, "bathroomsTooLow"),
      number_of_bedrooms: z.number().min(1, "bedroomsTooLow"),
      interior_space: z.number().min(1, "interiorTooLow"),
      levels: z.string().min(1, "levelsTooLow"),
      total_structure_area: z.number().min(1, "totalStructureTooLow"),
      lot_size:
        type === TypeEnum.APARTMENT
          ? z.number().optional()
          : z.number().min(1, "lotSizeTooLow"),
      year_built: z
        .number()
        .min(1900, "yearBuiltInvalid")
        .max(new Date().getFullYear() + 6, "yearBuiltInvalid"),
      construction_materials: z.string().optional(),
    });
  }
  if (type === TypeEnum.OFFICE && purpose === PurposeEnum.FORSALE) {
    schema = schema.extend({
      interior_space: z.number().min(1, "interiorTooLow"),
      levels: z.string().min(1, "levelsTooLow"),
      total_structure_area: z.number().min(1, "totalStructureTooLow"),
      lot_size: z.number().optional(),
      buyers_fee: z
        .number()
        .min(0, "buyersFeeInvalid")
        .max(30, "buyersFeeInvalid"),
      year_built: z
        .number()
        .min(1900, "yearBuiltInvalid")
        .max(new Date().getFullYear() + 6, "yearBuiltInvalid"),
      construction_materials: z.string().optional(),
    });
  }
  if (type === TypeEnum.OFFICE && purpose === PurposeEnum.FORRENT) {
    schema = schema.extend({
      interior_space: z.number().min(1, "interiorTooLow"),
      levels: z.string().min(1, "levelsTooLow"),
      total_structure_area: z.number().min(1, "totalStructureTooLow"),
      lot_size: z.number().optional(),
      year_built: z
        .number()
        .min(1900, "yearBuiltInvalid")
        .max(new Date().getFullYear() + 6, "yearBuiltInvalid"),
      construction_materials: z.string().optional(),
    });
  }

  schema = schema.extend({
    map_location: z
      .object({
        lat: z.number().min(-90).max(90),
        lon: z.number().min(-180).max(180),
      })
      .nullable()
      .refine((data) => data !== null, {
        message: "locationNotSet",
      }),
    description: z.string().min(200, "descriptionTooShort"),
  });

  if (type !== TypeEnum.LAND) {
    schema = schema.extend({
      orientation: z
        .object({
          north: z.boolean().default(false),
          south: z.boolean().default(false),
          east: z.boolean().default(false),
          west: z.boolean().default(false),
        })
        .refine(
          (data) => {
            const count = Object.values(data).filter(Boolean).length;
            return count <= 2;
          },
          {
            message: "max2Orientations",
          }
        ),
    });
  }

  return schema;
};
